import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useDashboardStore from '../store/dashboardStore';
import {formatCurrency,formatUnits,formatPercent} from '../utils/formatters';

const {FiBarChart} = FiIcons;

const EmptyState = () => (
  <div className="h-80 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 mx-auto mb-4 bg-brand-muted2/20 rounded-lg flex items-center justify-center">
        <SafeIcon icon={FiBarChart} className="w-8 h-8 text-brand-muted" />
      </div>
      <h4 className="text-brand-muted text-sm font-medium mb-1">No Products</h4>
      <p className="text-brand-muted2 text-xs">Upload sales data to see product rankings</p>
    </div>
  </div>
);

const RankedModule = () => {
  const {
    rankedData,
    topBottomMode,
    topBottomMetric,
    setTopBottomConfig,
    monochrome,
    hasData,
    loading
  } = useDashboardStore();

  const metrics = [
    {key: 'revenue', label: 'Revenue', formatter: formatCurrency},
    {key: 'units', label: 'Qty Sold', formatter: formatUnits},
    {key: 'profit', label: 'GP', formatter: formatCurrency}
  ];

  const currentMetric = metrics.find(m => m.key === topBottomMetric) || metrics[0];
  
  // No need to reverse data anymore - it comes pre-sorted from the store
  const displayData = hasData && rankedData?.length > 0 ? rankedData : [];

  const handleMetricChange = (newMetric) => {
    console.log(`Changing metric from ${topBottomMetric} to ${newMetric}`);
    setTopBottomConfig({metric: newMetric});
  };

  const handleModeChange = (newMode) => {
    console.log(`Changing mode from ${topBottomMode} to ${newMode}`);
    setTopBottomConfig({mode: newMode});
  };

  return (
    <motion.div
      className="bg-brand-card rounded-card p-6 shadow-card"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.16, delay: 0.1 }}
    >
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-brand-fg">
          {topBottomMode} 10 Products by {currentMetric.label}
        </h3>
        
        <div className="flex items-center space-x-2">
          {/* Metric Selector */}
          <select
            value={topBottomMetric}
            onChange={(e) => handleMetricChange(e.target.value)}
            disabled={loading}
            className="bg-brand-muted2/10 text-brand-fg rounded-button px-2 py-1 text-sm border-none outline-none disabled:opacity-50"
          >
            {metrics.map((metric) => (
              <option key={metric.key} value={metric.key} className="bg-brand-card">
                {metric.label}
              </option>
            ))}
          </select>

          {/* Mode Toggle */}
          <div className="flex bg-brand-muted2/10 rounded-button p-1">
            {['TOP', 'BOTTOM'].map((mode) => (
              <motion.button
                key={mode}
                onClick={() => handleModeChange(mode)}
                disabled={loading}
                className={`px-2 py-1 rounded text-xs transition-colors disabled:opacity-50 ${
                  topBottomMode === mode
                    ? 'bg-brand-accent text-brand-bg'
                    : 'text-brand-muted hover:text-brand-fg'
                }`}
                whileHover={!loading ? { scale: 1.02 } : {}}
                whileTap={!loading ? { scale: 0.98 } : {}}
              >
                {mode}
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-brand-muted text-sm">Loading rankings...</p>
          </div>
        </div>
      ) : !hasData || displayData.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="overflow-hidden">
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-brand-card border-b border-brand-muted2/20">
                <tr>
                  <th className="text-left py-2 px-3 text-xs font-medium text-brand-muted uppercase tracking-wider w-12">
                    Rank
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-brand-muted uppercase tracking-wider">
                    Product Name
                  </th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-brand-muted uppercase tracking-wider w-20">
                    {currentMetric.label}
                  </th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-brand-muted uppercase tracking-wider w-16">
                    Share
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayData.map((item, index) => (
                  <motion.tr
                    key={`${item.name}-${topBottomMetric}-${topBottomMode}-${index}`}
                    className="border-b border-brand-muted2/10 hover:bg-brand-muted2/5 transition-colors"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.12, delay: index * 0.03 }}
                  >
                    <td className="py-3 px-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        (topBottomMode === 'TOP' && index < 3) || (topBottomMode === 'BOTTOM' && index >= displayData.length - 3)
                          ? 'bg-brand-accent text-brand-bg' 
                          : 'bg-brand-muted2/20 text-brand-muted'
                      }`}>
                        {index + 1}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div>
                        <p className="text-brand-fg text-sm font-medium leading-tight">
                          {item.name}
                        </p>
                        <p className="text-brand-muted text-xs">
                          Product
                        </p>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <p className="text-brand-fg text-sm font-semibold">
                        {currentMetric.formatter(item.value)}
                      </p>
                      {/* Show additional context based on selected metric */}
                      {topBottomMetric === 'units' && (
                        <p className="text-brand-muted text-xs">
                          {formatCurrency(item.revenue)} rev
                        </p>
                      )}
                      {topBottomMetric === 'profit' && (
                        <p className="text-brand-muted text-xs">
                          {formatCurrency(item.revenue)} rev
                        </p>
                      )}
                      {topBottomMetric === 'revenue' && (
                        <p className="text-brand-muted text-xs">
                          {formatUnits(item.units)} units
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div className="flex flex-col items-end">
                        <p className="text-brand-accent text-sm font-medium">
                          {formatPercent(item.percent)}
                        </p>
                        <div className="w-12 h-1 bg-brand-muted2/20 rounded-full mt-1">
                          <div 
                            className="h-full bg-brand-accent rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(item.percent, 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RankedModule;